import { useEffect, type FC, type PropsWithChildren } from "react";
import { AuthenticatedUserContext } from "src/contexts/authentication/AuthenticatedUserContext";
import type { GetUserResponse } from "src/api/Tmd.dtos";
import useAuthenticatedUser from "src/hooks/authentication/useAuthenticatedUser";
import type { SWRConfiguration } from "swr";
import * as Sentry from "@sentry/react";
import { createSentryAnonymousUser } from "src/sentryUser";
import { useComputedColorScheme } from "@mantine/core";
import OverlayLoading from "src/components/layouts/loading/OverlayLoading";

interface IAuthenticatedUserProviderProps extends PropsWithChildren {
    configuration?: SWRConfiguration<GetUserResponse, GetUserResponse>;
}

const AuthenticatedUserProvider: FC<IAuthenticatedUserProviderProps> = ({
    children,
    configuration,
}) => {
    const context = useAuthenticatedUser(configuration);

    const colorScheme = useComputedColorScheme();

    useEffect(() => {
        if (!context.data) {
            Sentry.setUser(createSentryAnonymousUser());
            return;
        }

        Sentry.setUser({
            id: context.data.username,
            username: context.data.username,
            fullName: context.data.fullName,
            authenticated: true,
        });

        return () => {
            Sentry.setUser(createSentryAnonymousUser());
        };
    }, [context.data]);

    if (!context.loading && !context.validating && !context.data) {
        window.location.href = `/auth/openiddict?redirect=${encodeURIComponent(location.pathname + location.search)}&theme=${colorScheme}`;

        return <OverlayLoading />;
    } else if (context.loading) {
        return <OverlayLoading />;
    }

    return (
        <AuthenticatedUserContext.Provider value={context}>
            {children}
        </AuthenticatedUserContext.Provider>
    );
};

export default AuthenticatedUserProvider;
